import React from 'react';
import DashboardAgreementsSection from './src/agreements/DashboardAgreementsSection.jsx';
import { AutoApproveReconfirmBlock } from './src/components/AutoApproveReconfirm.jsx';
import { deniedTickersSummaryForMapping } from './src/utils/riskParameters.js';

const periodLabels = { '7d': 'Last 7 days', '30d': 'Last 30 days', '90d': 'Last 90 days', '1y': 'Last year' };

const analyticsPeriodLabel = (period) => periodLabels[period] || 'Last 30 days';

/** Narrow-viewport dashboard: Step 1 trading config → Step 2 terms → Step 3 SMS (same gating as desktop). */
const MobileDashboard = ({
  userData,
  tradeHistory,
  tradingAnalytics = {},
  channelMappings = [],
  analyticsPeriod = '30d',
  onAnalyticsPeriodChange,
  isAdmin,
  onNavigate,
  loading,
  canAccessChannelConfig,
  canAccessTradeHistory,
  hasAcceptedTerms,
  hasAcceptedSms,
  hasTradingConfig,
  termsReminderMessage = '',
  smsReminderMessage = '',
  onOpenAutoApproveReconfirmModal = () => {},
  autoApproveReconfirmFromApi = null,
}) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const exec = tradingAnalytics?.execution_stats;
  const totalTrades = tradingAnalytics?.total_trades;
  const tradesLogged = exec?.total_logged ?? totalTrades ?? 0;
  const statsError = Boolean(tradingAnalytics?.error);
  const missingExecStats =
    !statsError && exec == null && typeof totalTrades === 'number' && totalTrades > 0;
  const fmtExecRej = (v) => {
    if (loading) return '…';
    if (statsError) return '—';
    if (missingExecStats) return '—';
    return v ?? 0;
  };
  const lastTrade = tradeHistory && tradeHistory.length > 0 ? tradeHistory[0] : null;

  return (
    <div className="w-full max-w-md mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 text-center mb-2">
          Shingami Trades
        </h2>
        {isAdmin && (
          <p className="text-center text-blue-600 text-sm font-medium">👑 Admin User</p>
        )}
      </div>

      {/* Trading connection (tokens) — distinct from multi-channel setup steps */}
      <div className="bg-white rounded-xl shadow-lg p-4 mb-4 border-2 border-gray-100">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-800">API credentials</h3>
          <span className={`text-xl ${userData?.hasTokens ? 'text-green-500' : 'text-yellow-500'}`}>
            {userData?.hasTokens ? '✅' : '⚠️'}
          </span>
        </div>
        <p className="text-xs text-gray-600 mt-1">
          {userData?.hasTokens
            ? 'Trading configuration detected'
            : 'Complete channel config below'}
        </p>
      </div>

      <p className="text-xs text-gray-500 mb-2">
        Trade counts from history. Rejected = broker only (not Discord button).
      </p>
      {typeof onAnalyticsPeriodChange === 'function' && (
        <label className="text-xs text-gray-600 flex items-center gap-2 mb-3">
          Period
          <select
            value={analyticsPeriod}
            onChange={(e) => onAnalyticsPeriodChange(e.target.value)}
            className="border border-gray-300 rounded-lg px-2 py-1 text-xs flex-1"
          >
            <option value="7d">7 days</option>
            <option value="30d">30 days</option>
            <option value="90d">90 days</option>
            <option value="1y">1 year</option>
          </select>
        </label>
      )}
      <p className="text-xs text-gray-400 mb-2">{analyticsPeriodLabel(analyticsPeriod)}</p>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-blue-50 rounded-xl p-3 border border-blue-200 text-center">
          <p className="text-[10px] text-blue-700 font-medium mb-1 leading-tight">Logged</p>
          <p className="text-lg font-bold text-blue-700">
            {loading ? '…' : statsError ? '—' : tradesLogged}
          </p>
        </div>
        <div className="bg-green-50 rounded-xl p-3 border border-green-200 text-center">
          <p className="text-[10px] text-green-700 font-medium mb-1 leading-tight">Executed</p>
          <p className="text-lg font-bold text-green-700">
            {fmtExecRej(exec?.executed)}
          </p>
        </div>
        <div className="bg-orange-50 rounded-xl p-3 border border-orange-200 text-center">
          <p className="text-[10px] text-orange-700 font-medium mb-1 leading-tight">Rejected</p>
          <p className="text-lg font-bold text-orange-700">
            {fmtExecRej(exec?.rejected)}
          </p>
        </div>
      </div>

      {channelMappings.length > 0 && (
        <div className="bg-white rounded-xl shadow p-4 mb-4 border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Channel settings</h3>
          <ul className="space-y-3">
            {channelMappings.map((m) => (
              <li
                key={m.id || m.channel_id}
                className="border-b border-gray-100 last:border-0 pb-3 last:pb-0 text-xs"
              >
                <p className="font-medium text-gray-900">
                  {m.channel_name ? `#${m.channel_name}` : 'Channel'}{' '}
                  <span className="font-mono text-gray-500">{m.channel_id}</span>
                </p>
                <p className="text-gray-600 mt-1">
                  {m.use_sandbox ? 'Sandbox' : 'Live'} · Dev {Number(m.price_deviation_limit ?? 10)}% ·
                  Max {m.max_daily_trades ?? 10}/day · Auto {m.auto_approve_enabled ? 'On' : 'Off'}
                </p>
                <p className="text-gray-600 mt-0.5">
                  Denied: {deniedTickersSummaryForMapping(m)}
                </p>
                {m.account_id && (
                  <p className="font-mono text-[10px] text-gray-500 mt-0.5">{m.account_id}</p>
                )}
                <AutoApproveReconfirmBlock
                  mapping={m}
                  apiReconfirmConfig={autoApproveReconfirmFromApi}
                  onOpenReconfirmModal={onOpenAutoApproveReconfirmModal}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      {lastTrade && (
        <div className="bg-gray-50 rounded-xl p-4 mb-4 border border-gray-200">
          <p className="text-xs text-gray-600 font-medium mb-2">Last trade (history load)</p>
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-semibold text-gray-800">{lastTrade.symbol}</p>
              <p className="text-xs text-gray-600">{lastTrade.action}</p>
            </div>
            <div className="text-right">
              <p className={`text-sm font-bold ${lastTrade.status === 'filled' ? 'text-green-600' : 'text-yellow-600'}`}>
                {lastTrade.status}
              </p>
              {lastTrade.price && (
                <p className="text-xs text-gray-600">{formatCurrency(lastTrade.price)}</p>
              )}
            </div>
          </div>
        </div>
      )}

      <section
        id="mobile-dashboard-setup-steps"
        className="scroll-mt-4"
        aria-label="Setup steps"
      >
        <DashboardAgreementsSection
          hasAcceptedTerms={hasAcceptedTerms}
          hasAcceptedSms={hasAcceptedSms}
          hasTradingConfig={hasTradingConfig}
          canAccessChannelConfig={canAccessChannelConfig}
          termsReminderMessage={termsReminderMessage}
          smsReminderMessage={smsReminderMessage}
          onOpenTerms={() => onNavigate('agreements_terms')}
          onOpenSms={() => onNavigate('agreements_sms')}
          onOpenTradingConfig={() => onNavigate('channel_config')}
        />
      </section>

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-gray-600 text-sm">Loading...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileDashboard;
